import React, { Component } from 'react';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import { navigate } from '@reach/router';
import USER_ROLES from '../common/userroles';
import Console from '../console';
import { hasAllowedPermission, isStockerNavGated } from '../../utils/stockerNavPermissions';
import DashboardCard from './DashboardCard';
import AgChart from './AgChart';
import DashboardListGrid from './DashboardListGrid';
import DashboardReportDialog from './DashboardReportDialog';
import { foldFiltersToSearchText } from '../grid/filterUtils';
import {
  PERIOD,
  PERIOD_OPTIONS,
  DELIVERY_TYPE_OPTIONS,
  ORDER_RANGE_OPTIONS,
  getPeriodDateRange,
  getOrderRangeMeta,
  formatDashlateLabel,
  formatSalesLabel,
  reverseChronological,
  filterLastCalendarMonths,
} from './dateRanges';
import {
  fetchDashlate,
  fetchSales,
  fetchDashlateDays,
  fetchDashlateTime,
  fetchBoxListPage,
  fetchLiveAgentsPage,
  fetchAverageTurnOver,
  fetchAverageTurnOverReport,
  fetchTransactionTime,
  fetchAverageTransactionTimeReport,
  fetchAdherenceAndRTS,
  fetchAdherenceAndRTSReport,
  fetchConsultationsDashboard,
  fetchConsultationsReports,
  fetchPatientDetails,
  fetchPatientEnrolledCount,
  fetchBoxOrdersChart,
  fetchBoxUtilizationReport,
  fetchStockAgeing,
  fetchStockAgeingReport,
  fetchPickupTransactions,
  getDashboardPermissions,
  isRetail,
} from './dashboardApi';
import { OpenTasksPanel } from '../workflowQueue/OpenTasksPanel';
import { invalidateQueueCache, loadQueueSummary } from '../workflowQueue/queueApi';
import { viewerFromSession } from '../workflowQueue/queueModel';
import { isNewUiHidden, subscribeHideNewUi } from '../../utils/hideNewUi';
import {
  getLocationColumnDefs,
  getLiveAgentColumnDefs,
  TURNOVER_REPORT_COLS,
  TX_TIME_REPORT_COLS,
  ADHERENCE_REPORT_COLS,
  CONSULTATION_REPORT_COLS,
  ORDERS_REPORT_COLS,
  STOCK_AGE_REPORT_COLS,
  PICKUP_REPORT_COLS,
  buildGaugeDonutOptions,
} from './dashboardColumns';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  title: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '2.125rem',
    fontWeight: 400,
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  switchBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: '#0a5d66',
    color: '#0a5d66',
    '&:hover': {
      borderColor: '#084a52',
      backgroundColor: 'rgba(10, 93, 102, 0.06)',
    },
  },
  select: {
    minWidth: 140,
    backgroundColor: '#fff',
  },
  patientChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#475569',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  infoIcon: {
    color: '#0a5d66',
    padding: 4,
  },
  kpiRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 8,
  },
  kpi: {
    fontSize: '0.875rem',
    color: '#64748b',
  },
  kpiValue: {
    fontWeight: 700,
    color: '#0f172a',
    marginRight: 4,
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    height: 420,
    overflowY: 'auto',
    paddingRight: 4,
  },
  taskItem: {
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 12px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
    '&:hover': {
      borderColor: '#0a5d66',
      boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)',
      transform: 'translateY(-1px)',
    },
    '&:focus-visible': {
      outline: '2px solid #0a5d66',
      outlineOffset: 2,
    },
  },
  taskMeta: {
    color: '#64748b',
    fontSize: '0.78rem',
    marginTop: 4,
  },
  emptyState: {
    height: 420,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    textAlign: 'center',
  },
  gaugeCenter: {
    position: 'absolute',
    left: '50%',
    top: '48%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    pointerEvents: 'none',
    zIndex: 1,
  },
  gaugeValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.1,
  },
  gaugeUnit: {
    fontSize: '0.75rem',
    color: '#64748b',
  },
});

const CHART_PALETTE = {
  rxDeliver: '#A7F3D0',
  rxPickup: '#34D399',
  rxCompleted: '#047857',
  custDeliver: '#BFDBFE',
  custPickup: '#60A5FA',
  custCompleted: '#2563EB',
};

const selectSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
};

const PICKUP_TASK_SYNC_KEY = 'pickupChangeTasksUpdatedAt';

function parseAverageCount(payload) {
  if (!payload) return 0;
  const raw = payload.AverageCount;
  if (raw === '' || raw == null || raw === 'null' || raw === 'undefined') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

class Dashboard2 extends Component {
  locationsFilterBody = { pageNo: 1, pageSize: 20, searchtext: '' };
  agentsFilterBody = { pageNo: 1, pageSize: 25 };

  state = {
    permissions: getDashboardPermissions(),
    retail: isRetail(),
    // Defaults: all calendar period filters start on This Week.
    utilPeriod: PERIOD.THIS_WEEK,
    deliveryType: 'all',
    utilLoading: false,
    utilData: [],
    patientCount: 0,
    salesPeriod: PERIOD.THIS_WEEK,
    salesLoading: false,
    salesData: [],
    totalPrescriptions: 0,
    totalSales: 0,
    volumePeriod: PERIOD.THIS_WEEK,
    volumeLoading: false,
    volumeData: [],
    volumeTimePeriod: PERIOD.THIS_WEEK,
    volumeTimeLoading: false,
    volumeTimeData: [],
    locationSearch: '',
    locationsTotal: 0,
    locationsRefreshKey: 0,
    agentsTotal: 0,
    agentsRefreshKey: 0,
    turnPeriod: PERIOD.THIS_WEEK,
    turnLoading: false,
    turnDays: 0,
    txPeriod: PERIOD.THIS_WEEK,
    txLoading: false,
    txSeconds: 0,
    adherencePeriod: PERIOD.THIS_WEEK,
    adherenceLoading: false,
    adherenceData: [],
    consultPeriod: PERIOD.THIS_WEEK,
    consultLoading: false,
    consultStats: { totalcount: 0, Misscall: 0, avgDuration: 0 },
    consultChart: [],
    patientPeriod: PERIOD.THIS_WEEK,
    patientLoading: false,
    patientData: [],
    patientEnrolled: 0,
    ordersRange: '7d',
    ordersLoading: false,
    ordersData: [],
    stockLoading: false,
    stockData: [],
    pickupTasksLoading: false,
    pickupTasksLoadingMore: false,
    pickupTasks: [],
    pickupTasksSummary: {},
    pickupTasksTotal: 0,
    pickupTasksPage: 0,
    pickupTasksHasMore: false,
    report: {
      open: false,
      kind: null,
      title: '',
      loading: false,
      rows: [],
      columns: [],
      tabs: null,
      activeTab: 'pharmacy',
      pharmacyRows: [],
      opticalRows: [],
    },
  };

  locationSearchTimeout = null;

  componentDidMount() {
    this._unsubHideNewUi = subscribeHideNewUi(() => this.forceUpdate());
    if (isStockerNavGated() && !hasAllowedPermission({ entityType: 'Dashboard' })) {
      if (hasAllowedPermission({ name: 'Stocked Items' })) {
        navigate('/home/reports/stock-list/StockLists');
      } else if (hasAllowedPermission({ name: 'Picked Up Items' })) {
        navigate('/home/reports/pickup/PickUpLists');
      }
      return;
    }

    if (this.isDeliveryTech()) return;

    this.loadUtilization();
    this.loadSales();
    this.loadVolume();
    this.loadVolumeTime();
    this.loadTurnOver();
    this.loadTxTime();
    this.loadAdherence();
    this.loadPatientActivity();
    this.loadOrders();
    this.loadStockAge();
    this.loadPickupTasks();

    const { permissions, retail } = this.state;
    if (permissions.consultations && !retail) {
      this.loadConsultations();
    }

    this.agentsRefreshTimer = setInterval(() => {
      if (!this.state.retail) {
        this.setState((s) => ({ agentsRefreshKey: s.agentsRefreshKey + 1 }));
      }
    }, 60000);
    this.locationsRefreshTimer = setInterval(() => {
      this.setState((s) => ({ locationsRefreshKey: s.locationsRefreshKey + 1 }));
    }, 60000);
    this.pickupTasksRefreshTimer = setInterval(this.loadPickupTasks, 60000);
    window.addEventListener('focus', this.handlePickupTaskSync);
    window.addEventListener('storage', this.handlePickupTaskStorageSync);
    window.addEventListener('pickup-change-tasks-updated', this.handlePickupTaskSync);
    document.addEventListener('visibilitychange', this.handlePickupTaskVisibilitySync);
  }

  componentWillUnmount() {
    if (this._unsubHideNewUi) this._unsubHideNewUi();
    if (this.locationSearchTimeout) clearTimeout(this.locationSearchTimeout);
    if (this.agentsRefreshTimer) clearInterval(this.agentsRefreshTimer);
    if (this.locationsRefreshTimer) clearInterval(this.locationsRefreshTimer);
    if (this.pickupTasksRefreshTimer) clearInterval(this.pickupTasksRefreshTimer);
    window.removeEventListener('focus', this.handlePickupTaskSync);
    window.removeEventListener('storage', this.handlePickupTaskStorageSync);
    window.removeEventListener('pickup-change-tasks-updated', this.handlePickupTaskSync);
    document.removeEventListener('visibilitychange', this.handlePickupTaskVisibilitySync);
  }

  isDeliveryTech = () => {
    try {
      return (
        JSON.parse(sessionStorage.getItem('permissionUser')) ===
        USER_ROLES.DELIVERYTECH
      );
    } catch (e) {
      return false;
    }
  };

  closeReport = () => {
    this.setState({
      report: {
        open: false,
        kind: null,
        title: '',
        loading: false,
        rows: [],
        columns: [],
        tabs: null,
        activeTab: 'pharmacy',
        pharmacyRows: [],
        opticalRows: [],
      },
    });
  };

  openSimpleReport = async ({ kind, title, columns, loader }) => {
    this.setState({
      report: {
        open: true,
        kind,
        title,
        loading: true,
        rows: [],
        columns,
        tabs: null,
        activeTab: 'pharmacy',
        pharmacyRows: [],
        opticalRows: [],
      },
    });
    try {
      const rows = await loader();
      this.setState((s) => ({
        report: { ...s.report, loading: false, rows: rows || [] },
      }));
    } catch (err) {
      console.error('[Dashboard] report', kind, err);
      this.setState((s) => ({
        report: { ...s.report, loading: false, rows: [] },
      }));
    }
  };

  // ——— loaders ———

  loadUtilization = async () => {
    const { utilPeriod, deliveryType } = this.state;
    const [fromdate, todate] = getPeriodDateRange(utilPeriod);
    this.setState({ utilLoading: true });
    try {
      let rows = await fetchDashlate({
        fromdate,
        todate,
        id: utilPeriod,
        deliverytype: deliveryType,
      });
      if (Number(utilPeriod) === PERIOD.LAST_3_MONTHS) {
        rows = filterLastCalendarMonths(rows, 3, (r) => ({
          year:
            r.date_part_Year != null
              ? r.date_part_Year
              : r.date_part_year != null
                ? r.date_part_year
                : new Date().getFullYear(),
          month: r.date_part,
        }));
      }
      let patientCount = 0;
      const mapped = rows.map((res) => {
        const label = formatDashlateLabel(res, utilPeriod);
        let tp = parseInt(res.t_count_pickup, 10) || 0;
        let td = parseInt(res.t_count_deliver, 10) || 0;
        let tc = parseInt(res.t_count_completed, 10) || 0;
        let cp = parseInt(res.c_count_pickup, 10) || 0;
        let cd = parseInt(res.c_count_deliver, 10) || 0;
        let cc = parseInt(res.c_count_completed, 10) || 0;
        const tCount = parseInt(res.t_count, 10) || 0;
        const cCount = parseInt(res.c_count, 10) || 0;
        if (
          tp === 0 &&
          td === 0 &&
          tc === 0 &&
          cp === 0 &&
          cd === 0 &&
          cc === 0 &&
          (tCount > 0 || cCount > 0)
        ) {
          tc = tCount;
          cc = cCount;
        }
        patientCount += cCount;
        return {
          period: label,
          rxDeliver: td,
          rxPickup: tp,
          rxCompleted: tc,
          custDeliver: cd,
          custPickup: cp,
          custCompleted: cc,
        };
      });
      // Apex streamChart reverses categories/series — same chronological order.
      this.setState({
        utilData: reverseChronological(mapped),
        patientCount,
        utilLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] utilization', err);
      this.setState({ utilData: [], patientCount: 0, utilLoading: false });
    }
  };

  loadSales = async () => {
    const { salesPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(salesPeriod);
    this.setState({ salesLoading: true });
    try {
      const parsed = await fetchSales({ fromdate, todate, id: salesPeriod });
      const summary =
        parsed &&
        parsed.Prescriptions &&
        parsed.Prescriptions[0] &&
        parsed.Prescriptions[0].total_prescriptions;
      let series =
        (parsed && parsed.Prescriptions && parsed.Prescriptions[1]) || [];
      if (Number(salesPeriod) === PERIOD.LAST_3_MONTHS) {
        series = filterLastCalendarMonths(
          Array.isArray(series) ? series : [],
          3,
          (r) => ({ year: r.date_year, month: r.date })
        );
      }
      const salesData = reverseChronological(
        (Array.isArray(series) ? series : []).map((row) => ({
          period: formatSalesLabel(row, salesPeriod),
          sales: parseFloat(row.total_sales) || 0,
        }))
      );
      this.setState({
        salesData,
        totalPrescriptions: (summary && summary.total_prescriptions) || 0,
        totalSales: (summary && summary.total_prescriptions_sales) || 0,
        salesLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] sales', err);
      this.setState({
        salesData: [],
        totalPrescriptions: 0,
        totalSales: 0,
        salesLoading: false,
      });
    }
  };

  loadVolume = async () => {
    const { volumePeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(volumePeriod);
    this.setState({ volumeLoading: true });
    try {
      const rows = await fetchDashlateDays({ fromdate, todate });
      this.setState({
        volumeData: rows.map((r) => ({
          day: r.day,
          prescriptions: parseInt(r.rx_count, 10) || 0,
          patients: parseInt(r.customer_count, 10) || 0,
        })),
        volumeLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] volume', err);
      this.setState({ volumeData: [], volumeLoading: false });
    }
  };

  loadVolumeTime = async () => {
    const { volumeTimePeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(volumeTimePeriod);
    this.setState({ volumeTimeLoading: true });
    try {
      const rows = await fetchDashlateTime({ fromdate, todate });
      // Hours 0–23 in API order (same as streamChartTwo).
      const byHour = {};
      (rows || []).forEach((r) => {
        byHour[Number(r.time)] = {
          time: `${String(r.time).padStart(2, '0')}:00`,
          prescriptions: parseInt(r.rx_count, 10) || 0,
          patients: parseInt(r.customer_count, 10) || 0,
        };
      });
      const volumeTimeData = Object.keys(byHour)
        .map(Number)
        .sort((a, b) => a - b)
        .map((h) => byHour[h]);
      this.setState({ volumeTimeData, volumeTimeLoading: false });
    } catch (err) {
      console.error('[Dashboard] volume time', err);
      this.setState({ volumeTimeData: [], volumeTimeLoading: false });
    }
  };

  loadTurnOver = async () => {
    const { turnPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(turnPeriod);
    this.setState({ turnLoading: true });
    try {
      const data = await fetchAverageTurnOver({ fromdate, todate });
      this.setState({ turnDays: parseAverageCount(data), turnLoading: false });
    } catch (err) {
      console.error('[Dashboard] turnover', err);
      this.setState({ turnDays: 0, turnLoading: false });
    }
  };

  loadTxTime = async () => {
    const { txPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(txPeriod);
    this.setState({ txLoading: true });
    try {
      const data = await fetchTransactionTime({ fromdate, todate });
      this.setState({ txSeconds: parseAverageCount(data), txLoading: false });
    } catch (err) {
      console.error('[Dashboard] tx time', err);
      this.setState({ txSeconds: 0, txLoading: false });
    }
  };

  loadAdherence = async () => {
    const { adherencePeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(adherencePeriod);
    this.setState({ adherenceLoading: true });
    try {
      const rows = await fetchAdherenceAndRTS({ fromdate, todate });
      // Old stremPieChart: REMOVE_RX → RTS, everything else → Adherence.
      this.setState({
        adherenceData: (rows || []).map((r) => ({
          name: r.name === 'REMOVE_RX' ? 'RTS' : 'Adherence',
          value: Number(r.total_count) || 0,
        })),
        adherenceLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] adherence', err);
      this.setState({ adherenceData: [], adherenceLoading: false });
    }
  };

  loadConsultations = async () => {
    const { consultPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(consultPeriod);
    this.setState({ consultLoading: true });
    try {
      const res = await fetchConsultationsDashboard({ fromdate, todate });
      this.setState({
        consultStats: {
          totalcount: (res && res.totalcount) || 0,
          Misscall: (res && res.Misscall) || 0,
          avgDuration: (res && res.avgDuration) || 0,
        },
        consultChart: Array.isArray(res && res.ChartData) ? res.ChartData : [],
        consultLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] consultations', err);
      this.setState({
        consultStats: { totalcount: 0, Misscall: 0, avgDuration: 0 },
        consultChart: [],
        consultLoading: false,
      });
    }
  };

  loadPatientActivity = async () => {
    const { patientPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(patientPeriod);
    this.setState({ patientLoading: true });
    try {
      const [rawRows, enrolled] = await Promise.all([
        fetchPatientDetails({ fromdate, todate, id: patientPeriod }),
        fetchPatientEnrolledCount().catch(() => 0),
      ]);
      let rows = rawRows || [];
      if (Number(patientPeriod) === PERIOD.LAST_3_MONTHS) {
        rows = filterLastCalendarMonths(rows, 3, (r) => ({
          year:
            r.date_part_Year != null
              ? r.date_part_Year
              : r.date_part_year != null
                ? r.date_part_year
                : new Date().getFullYear(),
          month: r.date_part,
        }));
      }
      this.setState({
        patientData: reverseChronological(
          rows.map((r) => ({
            period: formatDashlateLabel(r, patientPeriod),
            count: parseInt(r.count, 10) || 0,
          }))
        ),
        patientEnrolled: enrolled,
        patientLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] patient', err);
      this.setState({ patientData: [], patientLoading: false });
    }
  };

  loadOrders = async () => {
    const { ordersRange } = this.state;
    const { barChartDuration, barChartGroupBy, sinceDays } =
      getOrderRangeMeta(ordersRange);
    this.setState({ ordersLoading: true });
    try {
      const res = await fetchBoxOrdersChart({ sinceDays });
      const chartDates = Array.from({ length: barChartDuration }, (_, i) =>
        moment()
          .subtract(i, barChartGroupBy)
          .format('YYYY-MM' + (barChartGroupBy === 'day' ? '-DD' : ''))
      );
      const chartData = [];
      const _data = (res && res._data) || {};
      const opticalData = (res && res.opticalData) || {};

      if (barChartGroupBy !== 'day') {
        chartDates.forEach((chartDate) => {
          let stocked = 0;
          let picked_up = 0;
          let opticalStocked = 0;
          let opticalPickedUp = 0;
          Object.keys(_data)
            .filter((d) => d.startsWith(chartDate))
            .forEach((dateKey) => {
              (_data[dateKey] || []).forEach((o) => {
                stocked += parseInt(o.stocked, 10) || 0;
                picked_up += parseInt(o.picked_up, 10) || 0;
              });
            });
          Object.keys(opticalData)
            .filter((d) => d.startsWith(chartDate))
            .forEach((dateKey) => {
              (opticalData[dateKey] || []).forEach((o) => {
                opticalStocked += parseInt(o.stocked, 10) || 0;
                opticalPickedUp += parseInt(o.picked_up, 10) || 0;
              });
            });
          chartData.push({
            period: moment(chartDate).format('MMM YYYY'),
            rxStocked: stocked,
            opticalStocked,
            rxPickedUp: picked_up,
            opticalPickedUp,
          });
        });
      } else {
        chartDates.forEach((chartDate) => {
          let stocked = 0;
          let picked_up = 0;
          let opticalStocked = 0;
          let opticalPickedUp = 0;
          (_data[chartDate] || []).forEach((o) => {
            stocked += parseInt(o.stocked, 10) || 0;
            picked_up += parseInt(o.picked_up, 10) || 0;
          });
          (opticalData[chartDate] || []).forEach((o) => {
            opticalStocked += parseInt(o.stocked, 10) || 0;
            opticalPickedUp += parseInt(o.picked_up, 10) || 0;
          });
          chartData.push({
            period: moment(chartDate).format('ddd MM-DD'),
            rxStocked: stocked,
            opticalStocked,
            rxPickedUp: picked_up,
            opticalPickedUp,
          });
        });
      }
      this.setState({ ordersData: chartData.reverse(), ordersLoading: false });
    } catch (err) {
      console.error('[Dashboard] orders', err);
      this.setState({ ordersData: [], ordersLoading: false });
    }
  };

  loadStockAge = async () => {
    this.setState({ stockLoading: true });
    try {
      const res = await fetchStockAgeing();
      const categories = [];
      const pharmacy = [];
      const optical = [];
      ((res && res.PharmacyDetail) || []).forEach((item) => {
        if (!item.age_category) return;
        const idx = categories.indexOf(item.age_category);
        if (idx !== -1) {
          pharmacy[idx] += parseInt(item.total_stock, 10) || 0;
        } else {
          categories.push(item.age_category);
          pharmacy.push(parseInt(item.total_stock, 10) || 0);
          optical.push(0);
        }
      });
      ((res && res.OpticalDetail) || []).forEach((item) => {
        if (!item.age_category) return;
        const idx = categories.indexOf(item.age_category);
        if (idx !== -1) {
          optical[idx] += parseInt(item.total_stock, 10) || 0;
        } else {
          categories.push(item.age_category);
          pharmacy.push(0);
          optical.push(parseInt(item.total_stock, 10) || 0);
        }
      });
      const AGE_ORDER = [
        'Older than 10 days',
        '5-9 days old',
        '2-4 days old',
      ];
      this.setState({
        stockData: categories
          .map((age, i) => ({
            age,
            pharmacy: pharmacy[i] || 0,
            optical: optical[i] || 0,
          }))
          .sort((a, b) => {
            const ai = AGE_ORDER.indexOf(a.age);
            const bi = AGE_ORDER.indexOf(b.age);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          }),
        stockLoading: false,
      });
    } catch (err) {
      console.error('[Dashboard] stock age', err);
      this.setState({ stockData: [], stockLoading: false });
    }
  };

  loadPickupTasks = async ({ page: pageIndex = 0, append = false } = {}) => {
    const loadingKey = append ? 'pickupTasksLoadingMore' : 'pickupTasksLoading';
    if (this.state[loadingKey]) return;
    this.setState({ [loadingKey]: true });
    try {
      const built = await loadQueueSummary(viewerFromSession());
      this.setState({
        pickupTasks: built.items,
        pickupTasksSummary: built.summary,
        pickupTasksTotal: built.summary.total,
        pickupTasksPage: pageIndex,
        pickupTasksHasMore: false,
        [loadingKey]: false,
      });
    } catch (err) {
      console.error('[Dashboard] pickup tasks', err);
      this.setState({
        pickupTasks: append ? this.state.pickupTasks : [],
        pickupTasksSummary: append ? this.state.pickupTasksSummary : {},
        pickupTasksTotal: append ? this.state.pickupTasksTotal : 0,
        pickupTasksHasMore: false,
        [loadingKey]: false,
      });
    }
  };

  handlePickupTasksScroll = (event) => {
    const { pickupTasksHasMore, pickupTasksLoadingMore, pickupTasksPage } = this.state;
    if (!pickupTasksHasMore || pickupTasksLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48) {
      this.loadPickupTasks({ page: pickupTasksPage + 1, append: true });
    }
  };

  openPickupTask = (taskId) => {
    if (!taskId) return;
    navigate(`/home/taskmanager?taskId=${encodeURIComponent(taskId)}`);
  };

  handlePickupTaskSync = () => {
    invalidateQueueCache();
    this.loadPickupTasks();
  };

  handlePickupTaskStorageSync = (event) => {
    if (event && event.key === PICKUP_TASK_SYNC_KEY) {
      this.loadPickupTasks();
    }
  };

  handlePickupTaskVisibilitySync = () => {
    if (!document.hidden) {
      this.loadPickupTasks();
    }
  };

  // ——— handlers ———

  handleLocationSearch = (e) => {
    const locationSearch = e.target.value;
    this.setState({ locationSearch });
    if (this.locationSearchTimeout) clearTimeout(this.locationSearchTimeout);
    this.locationSearchTimeout = setTimeout(() => {
      this.locationsFilterBody.searchtext = locationSearch;
      this.setState((s) => ({ locationsRefreshKey: s.locationsRefreshKey + 1 }));
    }, 400);
  };

  openTurnoverReport = () => {
    const { turnPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(turnPeriod);
    this.openSimpleReport({
      kind: 'turnover',
      title: 'Average Turn Over Report',
      columns: TURNOVER_REPORT_COLS,
      loader: () =>
        fetchAverageTurnOverReport({ fromdate, todate, period: turnPeriod }),
    });
  };

  openTxTimeReport = () => {
    const { txPeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(txPeriod);
    this.openSimpleReport({
      kind: 'txtime',
      title: 'Average Transaction Time Report',
      columns: TX_TIME_REPORT_COLS,
      loader: () =>
        fetchAverageTransactionTimeReport({
          fromdate,
          todate,
          period: txPeriod,
        }),
    });
  };

  openAdherenceReport = () => {
    const { adherencePeriod } = this.state;
    const [fromdate, todate] = getPeriodDateRange(adherencePeriod);
    this.openSimpleReport({
      kind: 'adherence',
      title: 'Adherence vs RTS Report',
      columns: ADHERENCE_REPORT_COLS,
      loader: () => fetchAdherenceAndRTSReport({ fromdate, todate }),
    });
  };

  openConsultationsReport = (status = 'Report') => {
    const { consultPeriod } = this.state;
    this.openSimpleReport({
      kind: 'consultations',
      title: status === 'Missed' ? 'Missed Consultations' : 'Consultations Report',
      columns: CONSULTATION_REPORT_COLS,
      loader: async () => {
        const page = await fetchConsultationsReports({
          date: String(consultPeriod),
          page: 1,
          pageSize: 200,
          status,
        });
        return page.results;
      },
    });
  };

  openPickupsDialog = () => {
    const { utilPeriod, deliveryType } = this.state;
    this.openSimpleReport({
      kind: 'pickups',
      title: 'Pick Ups',
      columns: PICKUP_REPORT_COLS,
      loader: () =>
        fetchPickupTransactions({
          period: utilPeriod,
          deliverytype: deliveryType,
        }),
    });
  };

  openOrdersReport = async () => {
    const { ordersRange } = this.state;
    const { sinceDays } = getOrderRangeMeta(ordersRange);
    this.setState({
      report: {
        open: true,
        kind: 'orders',
        title: 'Orders by Period',
        loading: true,
        rows: [],
        columns: ORDERS_REPORT_COLS,
        tabs: [
          { value: 'pharmacy', label: 'Pharmacy' },
          { value: 'optical', label: 'Optical' },
        ],
        activeTab: 'pharmacy',
        pharmacyRows: [],
        opticalRows: [],
      },
    });
    try {
      // Match legacy dashboard: paginated shape { results, total }.
      // disablePagination:true returns a bare array — normalizeDetailRows handles both.
      const res = await fetchBoxUtilizationReport({
        sinceDays,
        limit: 200,
        currentPagePharmacy: 1,
        currentPageOptical: 1,
        disablePagination: false,
      });
      const pharmacyRows =
        (res && res.PharmacyDetail && res.PharmacyDetail.results) || [];
      const opticalRows =
        (res && res.OpticalDetail && res.OpticalDetail.results) || [];
      this.setState((s) => ({
        report: {
          ...s.report,
          loading: false,
          pharmacyRows,
          opticalRows,
          rows: pharmacyRows,
        },
      }));
    } catch (err) {
      console.error('[Dashboard] orders report', err);
      this.setState((s) => ({
        report: { ...s.report, loading: false, rows: [] },
      }));
    }
  };

  openStockAgeReport = async () => {
    this.setState({
      report: {
        open: true,
        kind: 'stock',
        title: 'Stock Age Report',
        loading: true,
        rows: [],
        columns: STOCK_AGE_REPORT_COLS,
        tabs: [
          { value: 'pharmacy', label: 'Pharmacy' },
          { value: 'optical', label: 'Optical' },
        ],
        activeTab: 'pharmacy',
        pharmacyRows: [],
        opticalRows: [],
      },
    });
    try {
      const res = await fetchStockAgeingReport({
        limit: 200,
        currentPagePharmacy: 1,
        currentPageOptical: 1,
        disablePagination: false,
      });
      const pharmacyRows =
        (res && res.PharmacyDetail && res.PharmacyDetail.results) || [];
      const opticalRows =
        (res && res.OpticalDetail && res.OpticalDetail.results) || [];
      this.setState((s) => ({
        report: {
          ...s.report,
          loading: false,
          pharmacyRows,
          opticalRows,
          rows: pharmacyRows,
        },
      }));
    } catch (err) {
      console.error('[Dashboard] stock report', err);
      this.setState((s) => ({
        report: { ...s.report, loading: false, rows: [] },
      }));
    }
  };

  handleReportTabChange = (_e, activeTab) => {
    this.setState((s) => ({
      report: {
        ...s.report,
        activeTab,
        rows:
          activeTab === 'optical' ? s.report.opticalRows : s.report.pharmacyRows,
      },
    }));
  };

  // ——— chart options ———

  getUtilizationOptions = () => {
    const { utilData } = this.state;
    const retail = isRetail();
    const rxLabel = retail ? 'Orders' : 'Prescriptions';
    const custLabel = retail ? 'Customers' : 'Patients';
    return {
      data: utilData,
      height: 320,
      legend: { position: 'bottom' },
      series: [
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'rxDeliver',
          yName: `Deliver (${rxLabel})`,
          stacked: true,
          stackGroup: 'rx',
          fill: CHART_PALETTE.rxDeliver,
        },
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'rxPickup',
          yName: `Pick Up (${rxLabel})`,
          stacked: true,
          stackGroup: 'rx',
          fill: CHART_PALETTE.rxPickup,
        },
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'rxCompleted',
          yName: `Completed (${rxLabel})`,
          stacked: true,
          stackGroup: 'rx',
          fill: CHART_PALETTE.rxCompleted,
        },
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'custDeliver',
          yName: `Deliver (${custLabel})`,
          stacked: true,
          stackGroup: 'cust',
          fill: CHART_PALETTE.custDeliver,
        },
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'custPickup',
          yName: `Pick Up (${custLabel})`,
          stacked: true,
          stackGroup: 'cust',
          fill: CHART_PALETTE.custPickup,
        },
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'custCompleted',
          yName: `Completed (${custLabel})`,
          stacked: true,
          stackGroup: 'cust',
          fill: CHART_PALETTE.custCompleted,
        },
      ],
      axes: [
        { type: 'category', position: 'bottom', label: { rotation: -45 } },
        { type: 'number', position: 'left', nice: true, min: 0 },
      ],
    };
  };

  getSalesOptions = () => ({
    data: this.state.salesData,
    height: 300,
    series: [
      {
        type: 'area',
        xKey: 'period',
        yKey: 'sales',
        yName: 'Sales',
        fill: '#0a5d66',
        stroke: '#0a5d66',
        fillOpacity: 0.25,
        marker: { enabled: true, size: 4 },
      },
    ],
    axes: [
      { type: 'category', position: 'bottom', label: { rotation: -45 } },
      {
        type: 'number',
        position: 'left',
        label: {
          formatter: ({ value }) =>
            `$${Number(value || 0).toLocaleString('en-US', {
              maximumFractionDigits: 0,
            })}`,
        },
      },
    ],
  });

  getVolumeOptions = () => ({
    data: this.state.volumeData,
    height: 320,
    legend: { position: 'bottom' },
    series: [
      {
        type: 'bar',
        xKey: 'day',
        yKey: 'prescriptions',
        yName: 'Prescriptions',
        fill: '#3b82f6',
      },
      {
        type: 'bar',
        xKey: 'day',
        yKey: 'patients',
        yName: 'Patients',
        fill: '#10b981',
      },
    ],
    axes: [
      { type: 'category', position: 'bottom' },
      { type: 'number', position: 'left', nice: true, min: 0 },
    ],
  });

  getVolumeTimeOptions = () => ({
    data: this.state.volumeTimeData,
    height: 320,
    legend: { position: 'bottom' },
    series: [
      {
        type: 'line',
        xKey: 'time',
        yKey: 'prescriptions',
        yName: 'Prescriptions',
        stroke: '#3b82f6',
        marker: { enabled: true, size: 4 },
      },
      {
        type: 'line',
        xKey: 'time',
        yKey: 'patients',
        yName: 'Patients',
        stroke: '#10b981',
        marker: { enabled: true, size: 4 },
      },
    ],
    axes: [
      { type: 'category', position: 'bottom', label: { rotation: -45 } },
      { type: 'number', position: 'left', nice: true, min: 0 },
    ],
  });

  getAdherenceOptions = () => {
    const { adherenceData } = this.state;
    const hasRts = adherenceData.some((d) => d.name === 'RTS');
    // Old Apex: [isRTS ? blue : green, green]
    const fills = hasRts ? ['rgb(51, 102, 204)', '#6cca98'] : ['#6cca98', '#6cca98'];
    return {
      data: adherenceData,
      height: 280,
      legend: { position: 'right' },
      series: [
        {
          type: 'pie',
          angleKey: 'value',
          legendItemKey: 'name',
          calloutLabelKey: 'name',
          fills,
          strokes: ['#fff', '#fff'],
        },
      ],
    };
  };

  getConsultOptions = () => ({
    data: this.state.consultChart.map((c) => ({
      status: c.status,
      value: Number(c.value) || 0,
      color: c.color,
    })),
    height: 260,
    legend: { position: 'bottom' },
    series: [
      {
        type: 'pie',
        angleKey: 'value',
        legendItemKey: 'status',
        calloutLabelKey: 'status',
        innerRadiusRatio: 0.55,
        fills: (this.state.consultChart || []).map((c) => c.color || '#29B6F6'),
        strokes: ['#fff'],
      },
    ],
  });

  getPatientOptions = () => ({
    data: this.state.patientData,
    height: 300,
    series: [
      {
        type: 'bar',
        xKey: 'period',
        yKey: 'count',
        yName: 'Patients',
        fill: '#6366f1',
      },
    ],
    axes: [
      { type: 'category', position: 'bottom', label: { rotation: -45 } },
      // Old Apex patient chart used min: 1
      { type: 'number', position: 'left', nice: true, min: 0 },
    ],
  });

  getOrdersOptions = () => ({
    data: this.state.ordersData,
    height: 320,
    legend: { position: 'bottom' },
    series: [
      {
        type: 'bar',
        xKey: 'period',
        yKey: 'rxStocked',
        yName: 'Rx Stocked',
        stacked: true,
        stackGroup: 'stocked',
        fill: '#4285f4',
      },
      {
        type: 'bar',
        xKey: 'period',
        yKey: 'opticalStocked',
        yName: 'Optical Stocked',
        stacked: true,
        stackGroup: 'stocked',
        fill: '#a1c2fa',
      },
      {
        type: 'bar',
        xKey: 'period',
        yKey: 'rxPickedUp',
        yName: 'Rx Picked Up',
        stacked: true,
        stackGroup: 'picked',
        fill: '#db4437',
      },
      {
        type: 'bar',
        xKey: 'period',
        yKey: 'opticalPickedUp',
        yName: 'Optical Picked Up',
        stacked: true,
        stackGroup: 'picked',
        fill: '#eda29b',
      },
    ],
    axes: [
      { type: 'category', position: 'bottom', label: { rotation: -45 } },
      { type: 'number', position: 'left', nice: true, min: 0 },
    ],
  });

  getStockOptions = () => ({
    data: this.state.stockData,
    height: 320,
    legend: { position: 'bottom' },
    series: [
      {
        type: 'bar',
        xKey: 'age',
        yKey: 'pharmacy',
        yName: 'Pharmacy Stock',
        fill: '#4285f4',
      },
      {
        type: 'bar',
        xKey: 'age',
        yKey: 'optical',
        yName: 'Optical Stock',
        fill: '#db4437',
      },
    ],
    axes: [
      { type: 'category', position: 'bottom' },
      { type: 'number', position: 'left', nice: true, min: 0 },
    ],
  });

  renderPeriodSelect = (value, onChange, options = PERIOD_OPTIONS) => (
    <TextField
      select
      size="small"
      value={value}
      onChange={onChange}
      className={this.props.classes.select}
      sx={selectSx}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );

  renderReportButton = (onClick) => (
    <Button size="small" variant="outlined" onClick={onClick} sx={{ textTransform: 'none' }}>
      Report
    </Button>
  );

  renderGaugeOverlay = (value, unit) => {
    const { classes } = this.props;
    return (
      <div className={classes.gaugeCenter}>
        <div className={classes.gaugeValue}>{value}</div>
        <div className={classes.gaugeUnit}>{unit}</div>
      </div>
    );
  };

  parsePickupTaskDetails = (taskDetails) => {
    if (!taskDetails || typeof taskDetails !== 'string') return null;
    try {
      const details = JSON.parse(taskDetails);
      return details && details.type === 'pickup_change_request' ? details : null;
    } catch (e) {
      return null;
    }
  };

  renderPickupTasks = () => {
    const { pickupTasks, pickupTasksLoading, pickupTasksSummary } = this.state;
    return (
      <OpenTasksPanel
        items={pickupTasks}
        summary={pickupTasksSummary}
        loading={pickupTasksLoading}
        onScroll={this.handlePickupTasksScroll}
      />
    );
  };

  render() {
    const { classes, t } = this.props;

    if (this.isDeliveryTech()) {
      return <Console />;
    }

    const {
      permissions,
      retail,
      utilPeriod,
      deliveryType,
      utilLoading,
      patientCount,
      salesPeriod,
      salesLoading,
      totalPrescriptions,
      totalSales,
      volumePeriod,
      volumeLoading,
      volumeTimePeriod,
      volumeTimeLoading,
      locationSearch,
      locationsTotal,
      locationsRefreshKey,
      agentsTotal,
      agentsRefreshKey,
      turnPeriod,
      turnLoading,
      turnDays,
      txPeriod,
      txLoading,
      txSeconds,
      adherencePeriod,
      adherenceLoading,
      adherenceData,
      consultPeriod,
      consultLoading,
      consultStats,
      patientPeriod,
      patientLoading,
      patientEnrolled,
      ordersRange,
      ordersLoading,
      stockLoading,
      pickupTasksLoading,
      pickupTasksTotal,
      report,
    } = this.state;

    const txMinutes = Math.round((Number(txSeconds) / 60) * 100) / 100;

    return (
      <div className={classes.root}>
        <Box className={classes.titleRow}>
          <Typography className={classes.title} sx={{ mb: 0 }}>
            {t('Leftbar.Dashboard')}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            className={classes.switchBtn}
            onClick={() => this.props.onSwitchUi && this.props.onSwitchUi('old')}
          >
            Switch to Old UI
          </Button>
        </Box>

        <Grid container spacing={2}>
          {!isNewUiHidden() ? (
          <Grid item xs={12}>
            <DashboardCard
              title="Workflow Queues"
              dense
              actions={
                <React.Fragment>
                  <Chip
                    size="small"
                    label={
                      pickupTasksLoading
                        ? "Loading…"
                        : `${pickupTasksTotal || 0} open`
                    }
                    sx={{ fontWeight: 600 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/home/taskmanager')}
                    sx={{ textTransform: 'none' }}
                  >
                    View all
                  </Button>
                </React.Fragment>
              }
            >
              {this.renderPickupTasks()}
            </DashboardCard>
          </Grid>
          ) : null}
          {permissions.weeklyTotal && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="Utilization by period"
                loading={utilLoading}
                actions={
                  <React.Fragment>
                    {this.renderPeriodSelect(utilPeriod, (e) =>
                      this.setState(
                        { utilPeriod: Number(e.target.value) },
                        this.loadUtilization
                      )
                    )}
                    {this.renderPeriodSelect(
                      deliveryType,
                      (e) =>
                        this.setState(
                          { deliveryType: e.target.value },
                          this.loadUtilization
                        ),
                      DELIVERY_TYPE_OPTIONS
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={this.openPickupsDialog}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#6cca98',
                        '&:hover': { backgroundColor: '#5bb887' },
                      }}
                    >
                      Pick Ups
                    </Button>
                    <Tooltip
                      title="This chart contains orders that have been picked up, delivered or completed."
                      arrow
                      placement="right"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#0a5d66',
                            color: '#fff',
                            fontSize: '0.8125rem',
                            maxWidth: 280,
                            px: 1.5,
                            py: 1,
                            '& .MuiTooltip-arrow': { color: '#0a5d66' },
                          },
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        className={classes.infoIcon}
                        aria-label="Chart info"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <span className={classes.patientChip}>
                      <PeopleOutlineIcon fontSize="small" />
                      {patientCount}
                    </span>
                  </React.Fragment>
                }
              >
                <AgChart
                  options={this.getUtilizationOptions()}
                  height={320}
                  fileName={`Utilization_${moment().format('YYYY-MM-DD')}`}
                />
              </DashboardCard>
            </Grid>
          )}

          {permissions.sales && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="Revenue by period"
                loading={salesLoading}
                actions={
                  <React.Fragment>
                    {this.renderPeriodSelect(salesPeriod, (e) =>
                      this.setState(
                        { salesPeriod: Number(e.target.value) },
                        this.loadSales
                      )
                    )}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      <strong>{totalPrescriptions}</strong> Total Prescriptions
                      {' · '}
                      <strong>
                        $
                        {Number(totalSales || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>{' '}
                      Total Sales
                    </Typography>
                  </React.Fragment>
                }
              >
                <AgChart
                  options={this.getSalesOptions()}
                  height={300}
                  fileName={`Revenue_${moment().format('YYYY-MM-DD')}`}
                />
              </DashboardCard>
            </Grid>
          )}

          {permissions.accounts && (
            <Grid item xs={12}>
              <DashboardCard
                title="Locations"
                actions={
                  <React.Fragment>
                    <TextField
                      size="small"
                      placeholder="Search locations"
                      value={locationSearch}
                      onChange={this.handleLocationSearch}
                      sx={{ minWidth: 180, ...selectSx }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Chip
                      size="small"
                      label={`${locationsTotal || 0} Locations`}
                      sx={{ fontWeight: 600 }}
                    />
                  </React.Fragment>
                }
              >
                <DashboardListGrid
                  columnDefs={getLocationColumnDefs()}
                  filterBody={this.locationsFilterBody}
                  fetchPage={fetchBoxListPage}
                  pageZeroBased={false}
                  cacheBlockSize={20}
                  paginationPageSize={20}
                  height={420}
                  rowHeight={52}
                  refreshKey={locationsRefreshKey}
                  applyFilterModel={(body, filterModel) => {
                    const text = foldFiltersToSearchText(filterModel);
                    if (text) {
                      body.searchtext = text;
                    } else if (!this.state.locationSearch) {
                      body.searchtext = '';
                    } else {
                      body.searchtext = this.state.locationSearch;
                    }
                  }}
                  onTotal={(total) => this.setState({ locationsTotal: total })}
                  getRowId={(p) =>
                    String(
                      (p.data && (p.data.id || p.data.__ssrmId)) ||
                        `${(p.data && p.data.name) || 'box'}`
                    )
                  }
                />
              </DashboardCard>
            </Grid>
          )}

          {permissions.dailySalesStates && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="Volume by day"
                loading={volumeLoading}
                actions={this.renderPeriodSelect(
                  volumePeriod,
                  (e) =>
                    this.setState(
                      { volumePeriod: Number(e.target.value) },
                      this.loadVolume
                    )
                )}
              >
                <AgChart
                  options={this.getVolumeOptions()}
                  height={320}
                  fileName={`VolumeByDay_${moment().format('YYYY-MM-DD')}`}
                />
              </DashboardCard>
            </Grid>
          )}

          {permissions.dailySalesStates && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="Volume by time"
                loading={volumeTimeLoading}
                actions={this.renderPeriodSelect(
                  volumeTimePeriod,
                  (e) =>
                    this.setState(
                      { volumeTimePeriod: Number(e.target.value) },
                      this.loadVolumeTime
                    )
                )}
              >
                <AgChart
                  options={this.getVolumeTimeOptions()}
                  height={320}
                  fileName={`VolumeByTime_${moment().format('YYYY-MM-DD')}`}
                />
              </DashboardCard>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <DashboardCard
              title="Average turn over"
              loading={turnLoading}
              actions={
                <React.Fragment>
                  {this.renderPeriodSelect(
                    turnPeriod,
                    (e) =>
                      this.setState(
                        { turnPeriod: Number(e.target.value) },
                        this.loadTurnOver
                      )
                  )}
                  {this.renderReportButton(this.openTurnoverReport)}
                </React.Fragment>
              }
            >
              <Box sx={{ position: 'relative' }}>
                <AgChart
                  options={buildGaugeDonutOptions({
                    value: turnDays,
                    max: 30,
                    label: 'Days',
                    unit: 'days',
                    color: '#6cca98',
                  })}
                  height={260}
                  fileName={`AverageTurnOver_${moment().format('YYYY-MM-DD')}`}
                />
                {this.renderGaugeOverlay(turnDays, 'days')}
              </Box>
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <DashboardCard
              title="Adherence vs RTS"
              loading={adherenceLoading}
              actions={
                <React.Fragment>
                  {this.renderPeriodSelect(adherencePeriod, (e) =>
                    this.setState(
                      { adherencePeriod: Number(e.target.value) },
                      this.loadAdherence
                    )
                  )}
                  {this.renderReportButton(this.openAdherenceReport)}
                </React.Fragment>
              }
            >
              {adherenceData.length === 0 && !adherenceLoading ? (
                <Box sx={{ textAlign: 'center', color: '#94a3b8', pt: 8 }}>
                  No data available
                </Box>
              ) : (
                <AgChart
                  options={this.getAdherenceOptions()}
                  height={280}
                  fileName={`Adherence_${moment().format('YYYY-MM-DD')}`}
                />
              )}
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <DashboardCard
              title="Average transaction time"
              loading={txLoading}
              actions={
                <React.Fragment>
                  {this.renderPeriodSelect(
                    txPeriod,
                    (e) =>
                      this.setState(
                        { txPeriod: Number(e.target.value) },
                        this.loadTxTime
                      )
                  )}
                  {this.renderReportButton(this.openTxTimeReport)}
                </React.Fragment>
              }
            >
              <Box sx={{ position: 'relative' }}>
                <AgChart
                  options={buildGaugeDonutOptions({
                    value: txMinutes,
                    max: 5,
                    label: 'Minutes',
                    unit: 'min',
                    color: '#0a5d66',
                  })}
                  height={260}
                  fileName={`AverageTransactionTime_${moment().format('YYYY-MM-DD')}`}
                />
                {this.renderGaugeOverlay(txSeconds, 'seconds')}
              </Box>
            </DashboardCard>
          </Grid>

          {!retail && (
            <Grid item xs={12}>
              <DashboardCard
                title="Live Agents"
                actions={
                  <Chip
                    size="small"
                    label={`${agentsTotal || 0} Users`}
                    sx={{ fontWeight: 600 }}
                  />
                }
              >
                <DashboardListGrid
                  columnDefs={getLiveAgentColumnDefs()}
                  filterBody={this.agentsFilterBody}
                  fetchPage={fetchLiveAgentsPage}
                  pageZeroBased={false}
                  cacheBlockSize={25}
                  paginationPageSize={25}
                  height={420}
                  refreshKey={agentsRefreshKey}
                  applyFilterModel={(body, filterModel) => {
                    const text = foldFiltersToSearchText(filterModel);
                    body.searchtext = text || '';
                  }}
                  onTotal={(total) => this.setState({ agentsTotal: total })}
                  getRowId={(p) =>
                    String(
                      (p.data &&
                        (p.data.__ssrmId ||
                          `${p.data.account || ''}-${p.data.name || ''}-${
                            p.data.last_login_on || ''
                          }`)) ||
                        'agent'
                    )
                  }
                />
              </DashboardCard>
            </Grid>
          )}

          {permissions.consultations && !retail && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="Consultations"
                loading={consultLoading}
                actions={
                  <React.Fragment>
                    {this.renderPeriodSelect(consultPeriod, (e) =>
                      this.setState(
                        { consultPeriod: Number(e.target.value) },
                        this.loadConsultations
                      )
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => this.openConsultationsReport('Missed')}
                      sx={{ textTransform: 'none' }}
                    >
                      Missed ({consultStats.Misscall || 0})
                    </Button>
                    {this.renderReportButton(() =>
                      this.openConsultationsReport('Report')
                    )}
                  </React.Fragment>
                }
              >
                <div className={classes.kpiRow}>
                  <span className={classes.kpi}>
                    <span className={classes.kpiValue}>
                      {consultStats.totalcount}
                    </span>
                    Total
                  </span>
                  <span className={classes.kpi}>
                    <span className={classes.kpiValue}>
                      {consultStats.avgDuration}
                    </span>
                    Avg duration
                  </span>
                </div>
                <AgChart
                  options={this.getConsultOptions()}
                  height={260}
                  fileName={`Consultations_${moment().format('YYYY-MM-DD')}`}
                />
              </DashboardCard>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <DashboardCard
              title="Patient activity"
              loading={patientLoading}
              actions={
                <React.Fragment>
                  {this.renderPeriodSelect(patientPeriod, (e) =>
                    this.setState(
                      { patientPeriod: Number(e.target.value) },
                      this.loadPatientActivity
                    )
                  )}
                  <Chip
                    size="small"
                    label={`${patientEnrolled || 0} Enrolled`}
                    sx={{ fontWeight: 600 }}
                  />
                </React.Fragment>
              }
            >
              <AgChart
                options={this.getPatientOptions()}
                height={300}
                fileName={`PatientActivity_${moment().format('YYYY-MM-DD')}`}
              />
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <DashboardCard
              title="Orders by period"
              loading={ordersLoading}
              actions={
                <React.Fragment>
                  {this.renderPeriodSelect(
                    ordersRange,
                    (e) =>
                      this.setState(
                        { ordersRange: e.target.value },
                        this.loadOrders
                      ),
                    ORDER_RANGE_OPTIONS
                  )}
                  {this.renderReportButton(this.openOrdersReport)}
                </React.Fragment>
              }
            >
              <AgChart
                options={this.getOrdersOptions()}
                height={320}
                fileName={`OrdersByPeriod_${moment().format('YYYY-MM-DD')}`}
              />
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <DashboardCard
              title="Stock age"
              loading={stockLoading}
              actions={this.renderReportButton(this.openStockAgeReport)}
            >
              <AgChart
                options={this.getStockOptions()}
                height={320}
                fileName={`StockAge_${moment().format('YYYY-MM-DD')}`}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <DashboardReportDialog
          open={report.open}
          title={report.title}
          onClose={this.closeReport}
          loading={report.loading}
          columnDefs={report.columns}
          rowData={report.rows}
          tabs={report.tabs}
          activeTab={report.activeTab}
          onTabChange={this.handleReportTabChange}
          getRowId={(p) =>
            String(
              (p.data &&
                (p.data.id ||
                  p.data.__ssrmId ||
                  JSON.stringify(p.data).slice(0, 80))) ||
                Math.random()
            )
          }
        />
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(Dashboard2));



// WEBPACK FOOTER //
// ./src/components/dashboard2/index.js